import { FormEvent, useEffect, useMemo, useState } from 'react';
import { get, patch } from '../api';
import { useData } from '../App';
import { GRID, isPast, noWorkReason, pedAllowed, POINTS, slotLabel, SLOTS, stockLvl, TI_ROOM, todayIso, UNIT_LBL, UNIT_ORDER } from '../lib';
import { PeoplePicker, Picked } from '../PeoplePicker';
import type { Day, MyBooking, TiRange } from '../types';
import { IconKey, IconWarn, Modal, useFail, useToast } from '../ui';

/**
 * Edição completa: data, horário, sala, assunto, formato e participantes. Numa série, só a data
 * editada muda. A sala nunca é trocada sozinha: se a escolhida ficou ocupada, a pessoa decide.
 */
export function EditDialog({ booking: b, onClose, onSaved, manager }: { booking: MyBooking; onClose: () => void; onSaved: (msg: string) => void; manager?: boolean }) {
  const { catalog, events, me, roomById } = useData();
  const toast = useToast(), fail = useFail();
  const isTI = b.roomId === TI_ROOM;
  const [date, setDate] = useState(b.date);
  const [start, setStart] = useState(b.start);
  const [end, setEnd] = useState(b.end - 1);
  const [roomId, setRoomId] = useState(b.roomId);
  const [manual, setManual] = useState(false);
  const [title, setTitle] = useState(b.title);
  const [mode, setMode] = useState(b.mode);
  const [notes, setNotes] = useState(b.notes ?? '');
  const [people, setPeople] = useState<Picked[]>(b.participants.map(p => ({ ...p })));
  const [qty, setQty] = useState(b.qty ?? 1);
  const [notify, setNotify] = useState(true);
  const [day, setDay] = useState<Day | null>(null);
  const [ti, setTi] = useState<TiRange | null>(null);
  const [saving, setSaving] = useState(false);

  const block = !date ? 'Escolha uma data.' : date < todayIso() ? 'Essa data já passou. Escolha uma data a partir de hoje.'
    : noWorkReason(events, date) ? `Não há expediente nesse dia (${noWorkReason(events, date)}). Escolha outra data.`
    : isPast(date, start) ? 'Esse horário já passou. Escolha outro.' : null;

  useEffect(() => { if (!block) get<Day>('day?date=' + date).then(setDay).catch(fail); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setTi(null);
    if (isTI && !block) get<TiRange>(`notebooks/range?date=${date}&start=${start}&end=${end + 1}&except=${b.id}`).then(setTi).catch(fail);
  }, [date, start, end]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo(() => {
    if (!day) return [];
    // Pela gestão: só as salas que a pessoa gerencia; para quem reservou: a regra das salas pedagógicas da unidade
    return catalog.rooms.filter(r => r.id !== TI_ROOM && (manager ? me.manageRooms.includes(r.id) : pedAllowed(r, me.unit))).map(r => {
      const free = !day.bookings.some(x => x.roomId === r.id && x.id !== b.id && x.start <= end && x.end > start);
      return { r, free };
    });
  }, [day, start, end]); // eslint-disable-line react-hooks/exhaustive-deps
  const keep = manual ? roomId : b.roomId;
  const keepO = options.find(o => o.r.id === keep);
  const chosen = isTI ? TI_ROOM : keepO?.free ? keep : manual ? roomId : '';
  const nOk = options.filter(o => o.free).length;
  const room = roomById(chosen);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (block) { toast(block); return; }
    if (!chosen) { toast('Escolha uma sala disponível.'); return; }
    if (isTI && (!ti?.msOk || qty < 1 || qty > ti.max)) { toast(ti?.msOk ? `Informe uma quantidade entre 1 e ${ti.max}.` : 'Não há notebooks da TI para esse período.'); return; }
    setSaving(true);
    try {
      await patch(`bookings/${b.id}`, { date, start, end: end + 1, roomId: chosen, title: title.trim() || b.title, mode, notes: notes.trim(),
        participants: people.map(p => ({ email: p.email, name: p.name, external: p.external })), qty: isTI ? qty : undefined, notify });
      onSaved(`Reserva alterada${b.seriesId ? ' (só esta data da série)' : ''}.${chosen !== b.roomId && room ? ' Chave: ' + POINTS[room.keyPoint] + '.' : ''}`);
    } catch (err) { fail(err); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} labelledBy="editTitle">
      <form onSubmit={submit} noValidate>
        <div className="dlg-h"><h3 id="editTitle">Editar reserva</h3><p>{b.title}</p></div>
        <div className="dlg-b">
          {b.seriesId && <div className="note warn"><IconWarn /><span>Esta reserva faz parte de uma série. A alteração vale só para esta data; as outras continuam iguais.</span></div>}
          <div className="row">
            <div className="field"><label htmlFor="eDate">Data</label><input type="date" id="eDate" min={todayIso()} value={date} onChange={ev => setDate(ev.target.value)} /></div>
            <div className="field"><label htmlFor="eStart">Início</label>
              <select id="eStart" value={start} onChange={ev => { const v = +ev.target.value; setStart(v); if (end < v) setEnd(v); }}>
                {Array.from({ length: SLOTS }, (_, i) => i).filter(i => i % GRID === 0 || i === start).map(i => <option key={i} value={i} disabled={!!date && isPast(date, i)}>{slotLabel(i)}</option>)}
              </select></div>
            <div className="field"><label htmlFor="eEnd">Término</label>
              <select id="eEnd" value={end} onChange={ev => setEnd(+ev.target.value)}>
                {Array.from({ length: SLOTS - start }, (_, k) => start + k).filter(v => (v + 1) % GRID === 0 || v === end).map(v => <option key={v} value={v}>{slotLabel(v + 1)}</option>)}
              </select></div>
          </div>
          {block && <div className="note warn"><IconWarn /><span>{block}</span></div>}
          {isTI ? <>
            {!block && ti && !ti.msOk && <div className="note warn"><IconWarn /><span>{ti.own ? 'Você já reservou a Sala Microsoft nesse período.' : 'A Sala Microsoft está livre no período todo: reserve a sala, que tem prioridade.'}</span></div>}
            <div className="field"><label htmlFor="eQty">Quantidade de notebooks</label>
              <input type="number" id="eQty" min={1} max={ti?.max ?? 1} value={qty} onChange={ev => setQty(Math.floor(+ev.target.value || 0))} />
              <span className="hint">{ti?.msOk ? `${ti.max} disponíveis nesse período · ${stockLvl(ti.max)[1]}` : ''}</span></div>
          </> : (
            <div className="field"><label htmlFor="eRoom">Sala</label>
              <select id="eRoom" value={chosen} onChange={ev => { setManual(true); setRoomId(ev.target.value); }} disabled={!!block}>
                {!chosen && <option value="">{block ? 'Sem salas para esta data' : 'Escolha uma sala disponível'}</option>}
                {UNIT_ORDER.map(u => {
                  const g = options.filter(o => o.r.unit === u);
                  return g.length ? <optgroup key={u} label={UNIT_LBL[u]}>{g.map(o => <option key={o.r.id} value={o.r.id} disabled={!o.free}>{o.r.name}{o.free ? '' : ' · ocupada'}</option>)}</optgroup> : null;
                })}
              </select>
              <span className="hint">{block ? '' : !nOk ? 'Nenhuma sala disponível nesse período. Mude a data ou o horário.'
                : keepO && !keepO.free ? `A ${keepO.r.name} está ocupada nesse período. Escolha outra sala (${nOk} disponíveis) ou mude o horário.` : `${nOk} salas disponíveis nesse período.`}</span></div>
          )}
          <div className="field"><label htmlFor="eTitle">Assunto</label><input type="text" id="eTitle" maxLength={120} value={title} onChange={ev => setTitle(ev.target.value)} /></div>
          {!isTI && room?.category !== 'pedagogica' && <>
            <div className="field"><span className="lbl">Formato</span>
              <div className="opts">
                <label><input type="radio" name="emode" checked={mode === 'presencial'} onChange={() => setMode('presencial')} /> Presencial</label>
                <label><input type="radio" name="emode" checked={mode === 'online'} onChange={() => setMode('online')} /> Online (Teams)</label>
              </div></div>
            <div className="field"><label htmlFor="eP">Participantes</label>
              <PeoplePicker id="eP" placeholder="Nome de um colega ou e-mail do responsável" allowExternal exclude={people.map(p => p.email)}
                onPick={p => people.some(x => x.email === p.email) ? toast(`${p.name} já está na lista de participantes.`) : setPeople([...people, p])} />
              <div className="people">
                {people.length ? people.map((p, k) => <div className="person" key={p.email}><span className="nm">{p.name}<small>{p.external ? 'Externo' : p.email}</small></span><span />
                  <button type="button" className="x" aria-label={`Remover ${p.name}`} onClick={() => setPeople(people.filter((_, j) => j !== k))}>×</button></div>)
                  : <span className="hint">Nenhum participante.</span>}
              </div></div>
          </>}
          <div className="field"><label htmlFor="eNotes">Observações</label><textarea id="eNotes" value={notes} onChange={ev => setNotes(ev.target.value)} maxLength={500} /></div>
          {room && <div className="note key"><IconKey /><span><b>{isTI ? 'Notebooks' : 'Chave'}:</b> {isTI ? 'Retire e devolva os notebooks na sala da TI.' : <>Retire a chave na <b>{POINTS[room.keyPoint]}</b> antes do início e devolva no mesmo local ao fim.</>}</span></div>}
          {!isTI && <label className="check"><input type="checkbox" checked={notify} onChange={ev => setNotify(ev.target.checked)} /> Avisar os participantes da alteração pelo Outlook</label>}
        </div>
        <div className="dlg-f">
          <button type="button" className="btn ghost" onClick={onClose}>Voltar</button>
          <button type="submit" className="btn" disabled={saving || !!block || !chosen}>{saving ? 'Salvando…' : 'Salvar alteração'}</button>
        </div>
      </form>
    </Modal>
  );
}
